import axios from "axios";
import Pagination from "Components/Pagination/Pagination";
import DeleteUserpopup from "Components/Users/DeleteUserPopup";
import EditUserpopup from "Components/Users/EditUserpopup";
import ChangeAvatarModal from "Components/Users/ChangeAvatarModal";
import UserDetailSkeleton from "Components/Users/UserDetailSkeleton";
import React, { useEffect, useState } from "react";
import { BiSolidLogInCircle, BiTrash } from "react-icons/bi";
import { FaEdit, FaMobileAlt, FaCamera } from "react-icons/fa";
import { FiArrowDownLeft, FiArrowUpRight, FiSearch } from "react-icons/fi";
import { IoMdChatbubbles } from "react-icons/io";
import { useNavigate } from "react-router-dom";
import { HiFilter } from "react-icons/hi";
import { IoMdTrophy } from "react-icons/io";
import { MdTimeline } from "react-icons/md";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FiCalendar } from "react-icons/fi";
import { usePermission } from "Context/PermissionContext";
import { MdBlock } from "react-icons/md";
import { CgUnblock } from "react-icons/cg";

const COINS_PER_MINUTE = {
  audio: 8,
  video: 40,
};

// ===== Skeleton Components =====
const CallHistorySkeleton = () => (
  <div className="animate-pulse space-y-4 rounded-2xl bg-white p-4 shadow-sm sm:p-6">
    {[...Array(3)].map((_, i) => (
      <div key={i} className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="h-12 w-12 rounded-full bg-gray-300"></div>
          <div>
            <div className="mb-2 h-4 w-32 rounded bg-gray-300"></div>
            <div className="h-3 w-20 rounded bg-gray-200"></div>
          </div>
        </div>
        <div className="text-right">
          <div className="mb-2 h-3 w-24 rounded bg-gray-300"></div>
          <div className="h-3 w-16 rounded bg-gray-200"></div>
        </div>
      </div>
    ))}
  </div>
);

const RechargeHistorySkeleton = () => (
  <div className="animate-pulse rounded-2xl bg-white p-4 shadow-sm sm:p-6">
    <table className="w-full min-w-[700px]">
      <thead>
        <tr>
          {[...Array(7)].map((_, i) => (
            <th key={i} className="px-4 py-3">
              <div className="h-4 w-20 rounded bg-gray-300"></div>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {[...Array(3)].map((_, row) => (
          <tr key={row}>
            {[...Array(7)].map((_, col) => (
              <td key={col} className="px-4 py-3">
                <div className="h-4 w-16 rounded bg-gray-200"></div>
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const LoginHistorySkeleton = () => (
  <div className="animate-pulse space-y-3 rounded-2xl bg-white p-4 shadow-sm sm:p-6">
    {[...Array(3)].map((_, i) => (
      <div key={i} className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <div className="h-6 w-6 rounded-full bg-gray-300"></div>
          <div className="h-4 w-20 rounded bg-gray-300"></div>
        </div>
        <div className="h-5 w-32 rounded-full bg-gray-200"></div>
      </div>
    ))}
  </div>
);

// Helper to safely extract avatar URL whether it's a string, an object { avatarUrl }, or fallback to UI avatar
const getAvatarUrl = (avatar, name = "User") => {
  if (typeof avatar === "string" && avatar.trim().length > 0 && !avatar.includes("via.placeholder.com")) {
    return avatar;
  }
  if (avatar && typeof avatar === "object" && avatar.avatarUrl) {
    return avatar.avatarUrl;
  }
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(name || "User")}&background=c51369&color=fff`;
};

// ===== Main Component =====
const UserDetailsPage = ({ id }) => {
  const [userData, setUserData] = useState({});
  const [callHistory, setCallHistory] = useState([]);
  const [loginHistoryData, setLoginHistoryData] = useState([]);
  const [transactionHistory, setTransactionHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [callHistoryLoading, setCallHistoryLoading] = useState(false);
  const [rechargeLoading, setRechargeLoading] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);

  const [editUserModal, setEditUserModal] = useState(false);
  const [deleteUserModal, setDeleteUserModal] = useState(false);
  const [changeAvatarModal, setChangeAvatarModal] = useState(false);
  const [activeTab, setActiveTab] = useState("calls");
  const [activeCallTab, setActiveCallTab] = useState("audio");

  const [callFilter, setCallFilter] = useState("");
  const [selectdate, setSelectDate] = useState("");

  const [laserDateType, setLaserDateType] = useState("");
  const [laserSpecificDate, setLaserSpecificDate] = useState(null);
  const [laserFromDate, setLaserFromDate] = useState(null);
  const [laserToDate, setLaserToDate] = useState(null);
  const [ledgerData, setLedgerData] = useState([]);
  const [withdrawalData, setWithdrawalData] = useState([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerTab, setLedgerTab] = useState("Coins");

  // Activity tab state
  const [activityData, setActivityData] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);
  const [milestoneData, setMilestoneData] = useState(null);
  const [milestoneConfigs, setMilestoneConfigs] = useState([]);

  // Messages tab state
  const [chatContacts, setChatContacts] = useState([]);
  const [chatContactsLoading, setChatContactsLoading] = useState(false);
  const [chatContactsSearch, setChatContactsSearch] = useState("");
  const [selectedChatContact, setSelectedChatContact] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatMessagesLoading, setChatMessagesLoading] = useState(false);
  const [chatMessagesPagination, setChatMessagesPagination] = useState({
    page: 1,
    totalPages: 1,
    totalMessages: 0,
  });
  const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);

  const [loginHistory, setLoginHistory] = useState([]);
  const [loginPagination, setLoginPagination] = useState({
    page: 1,
    totalPages: 1,
    totalCount: 0,
  });

  // Ledger states

  const [ledgerPagination, setLedgerPagination] = useState({
    page: 1,
    totalPages: 1,
    totalCount: 0,
  });

  //Recharge history pagination
  const [rechargePagination, setRechargePagination] = useState({
    page: 1,
    totalPages: 1,
    totalCount: 0,
  });

  // Call history pagination
  const [audioPagination, setAudioPagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const [videoPagination, setVideoPagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0,
    hasNextPage: false,
    hasPrevPage: false,
  });

  const [withdrawalPagination, setWithdrawalPagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const { permissions } = usePermission();
  const currentTabPermissions = permissions?.users?.subModule?.user;

  const navigate = useNavigate();
  const rowsPerPage = 5;

  // #===========================API CALLING=====================#

  const getUserData = () => {
    setLoading(true);
    axios
      .get(`${process.env.REACT_APP_API_URL}/users/get-user-by-userId/${id}`)
      .then((res) => setUserData(res?.data?.User))
      .finally(() => setLoading(false));
  };

  const handleloginHistory = async (page = 1) => {
    setLoginLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/login-history/get-by-user-id/${id}`,
        { params: { page, limit: rowsPerPage } }
      );

      if (res.data.success) {
        setLoginHistory(res.data.data || []);
        setLoginPagination({
          page: res.data.pagination.page || 1,
          totalPages: res.data.pagination.totalPages || 1,
          totalCount: res.data.pagination.totalCount || 0,
        });
      } else {
        setLoginHistory([]);
        setLoginPagination({ page: 1, totalPages: 1, totalCount: 0 });
      }
    } catch (err) {
      console.error("Login history fetch error:", err);
      setLoginHistory([]);
      setLoginPagination({ page: 1, totalPages: 1, totalCount: 0 });
    } finally {
      setLoginLoading(false);
    }
  };

  const recharge = (page = 1) => {
    setRechargeLoading(true);
    axios
      .get(
        `${process.env.REACT_APP_API_URL}/transaction-history/get-transaction-histories/${id}`,
        {
          params: { page, limit: rowsPerPage },
        }
      )
      .then((res) => {
        setTransactionHistory(res?.data?.data || []);
        setRechargePagination({
          page: res?.data?.pagination?.page || 1,
          totalPages: res?.data?.pagination?.totalPages || 1,
          totalCount: res?.data?.pagination?.totalCount || 0,
        });
      })
      .catch((err) => console.log(err))
      .finally(() => setRechargeLoading(false));
  };

  const getLedgerData = async (page = 1) => {
    try {
      setLedgerLoading(true);

      const endPoint =
        ledgerTab === "Coins"
          ? "get-all-ledger"
          : ledgerTab === "GiftCoins"
          ? "get-all-gift-ledger"
          : "";

      const params = {
        page,
        limit: rowsPerPage,
        dateType: laserDateType || "",
        specificDate: laserSpecificDate
          ? laserSpecificDate.toLocaleDateString("en-CA")
          : null,
        fromDate: laserFromDate
          ? laserFromDate.toLocaleDateString("en-CA")
          : null,
        toDate: laserToDate ? laserToDate.toLocaleDateString("en-CA") : null,
      };

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/ledger/${endPoint}/${id}`,
        { params }
      );

      if (res.data.success) {
        setLedgerData(res.data.data);
        setLedgerPagination({
          page: res.data.currentPage,
          totalPages: res.data.totalPages,
          totalCount: res.data.totalCount,
        });
      } else {
        setLedgerData([]);
        setLedgerPagination({ page: 1, totalPages: 1, totalCount: 0 });
      }
    } catch (error) {
      console.error("Failed to fetch ledger data:", error);
      setLedgerData([]);
      setLedgerPagination({ page: 1, totalPages: 1, totalCount: 0 });
    } finally {
      setLedgerLoading(false);
    }
  };

  const getWithdrawalTransactions = async (page = 1) => {
    try {
      setLedgerLoading(true);
      const params = {
        userId: id,
        page: page,
        limit: 5,
      };
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/tpi-payout/user/transactions/payment-status`,
        { params }
      );
      console.log("Response of getWithdrawalTransactions: ", response.data);
      setWithdrawalData(response.data.transactions);
      setWithdrawalPagination({
        page: response.data.currentPage,
        totalPages: response.data.totalPages,
        totalCount: response.data.totalCount,
      });
    } catch (error) {
      console.log("Error in getWithdrawalTransactions: ", error);
    } finally {
      setLedgerLoading(false);
    }
  };

  const updateBlockingStatus = async (deviceId, isBlocked) => {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/users/block-device/${userData._id}`,
        { deviceId: deviceId, isBlocked: isBlocked }
      );
      // console.log("Response of updateBlockingStatus: ", response.data);
      getUserData();
    } catch (error) {
      console.log("Error in updateBlockingStatus: ", error);
    }
  };

  const getCallHistory = (
    page = 1,
    callType = activeCallTab,
    filter = callFilter,
    selectedDate = selectdate
  ) => {
    setCallHistoryLoading(true);

    const now = new Date();
    let startDate = null;
    let endDate = null;

    if (filter === "today") {
      startDate = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        0,
        0,
        0
      );
      endDate = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        23,
        59,
        59
      );
    } else if (filter === "last7days") {
      startDate = new Date();
      startDate.setDate(now.getDate() - 7);
      endDate = now;
    } else if (filter === "thisMonth") {
      startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      endDate = now;
    } else if (filter === "lastMonth") {
      startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      endDate = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
    } else if (selectdate) {
      startDate = new Date(selectdate);
      startDate.setHours(0, 0, 0);
      endDate = new Date(selectdate);
      endDate.setHours(23, 59, 59);
    }

    axios
      .get(
        `${process.env.REACT_APP_API_URL}/call/call-histories/user-id/${id}`,
        {
          params: {
            page,
            limit: rowsPerPage,
            callType,
            startDate: startDate ? startDate.toISOString() : null,
            endDate: endDate ? endDate.toISOString() : null,
          },
        }
      )
      .then((res) => {
        setCallHistory(res?.data?.data || []);

        const paginationData = {
          page: res?.data?.pagination?.page || 1,
          totalPages: res?.data?.pagination?.totalPages || 1,
          total: res?.data?.pagination?.total || 0,
          hasNextPage: res?.data?.pagination?.hasNextPage || false,
          hasPrevPage: res?.data?.pagination?.hasPrevPage || false,
        };
        if (callType === "audio") setAudioPagination(paginationData);
        else setVideoPagination(paginationData);
      })
      .catch(() => {
        setCallHistory([]);
        const reset = {
          page: 1,
          totalPages: 1,
          total: 0,
          hasNextPage: false,
          hasPrevPage: false,
        };
        if (callType === "audio") setAudioPagination(reset);
        else setVideoPagination(reset);
      })
      .finally(() => setCallHistoryLoading(false));
  };

  const convertSeconds = (totalSeconds) => {
    if (totalSeconds < 60) {
      return `${totalSeconds} secs`;
    } else {
      const mins = Math.floor(totalSeconds / 60);
      const secs = totalSeconds % 60;

      return `${mins} mins ${secs} secs`;
    }
  };

  // #==================USE-EFFECT DEPENDECY FETCHING OF DATA===================#

  useEffect(() => {
    if (id) {
      getUserData();
    }
  }, [id]);

  useEffect(() => {
    console.log("LedgerTab in effect:", ledgerTab);

    if (activeTab === "calls") {
      getCallHistory(1, activeCallTab);
    } else {
      setSelectDate("");
      setCallFilter("");
    }
    if (activeTab === "login") handleloginHistory();
    if (activeTab === "recharge") {
      setCurrentPage(1);
      recharge();
    }
    if (activeTab === "Ledger") {
      if (ledgerTab !== "Withdrawal") {
        if (!laserDateType) {
          getLedgerData(1);
        } else if (laserDateType === "specific" && laserSpecificDate) {
          getLedgerData(1);
        } else if (laserDateType === "custom" && laserFromDate && laserToDate) {
          getLedgerData(1);
        }
      } else {
        getWithdrawalTransactions(1);
      }
    }
    if (activeTab === "activity") {
      fetchActivityData();
    }
    if (activeTab === "messages") {
      fetchChatContacts();
      setSelectedChatContact(null);
      setChatMessages([]);
      setChatContactsSearch("");
    }
  }, [
    activeTab,
    activeCallTab,
    selectdate,
    callFilter,
    laserDateType,
    laserSpecificDate,
    laserFromDate,
    laserToDate,
    ledgerTab,
  ]);

  const handleCallHistoryPageChange = (newPage) =>
    getCallHistory(newPage, activeCallTab);

  // ===== Messages Tab Functions =====
  const fetchChatContacts = async () => {
    setChatContactsLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/chat/get-chat-by-user-id/${id}`
      );
      // The API may return { chatList, pendingRequestsCount } or just an array
      const data = res?.data?.chatList || res?.data || [];
      setChatContacts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching chat contacts:", err);
      setChatContacts([]);
    } finally {
      setChatContactsLoading(false);
    }
  };

  const fetchChatMessages = async (contactId, page = 1, append = false) => {
    if (page === 1) setChatMessagesLoading(true);
    else setLoadingMoreMessages(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/chat/messages/${id}/${contactId}`,
        { params: { page, limit: 50 } }
      );
      const { data, totalPages, totalMessages, currentPage: cp } = res?.data || {};
      if (append && page > 1) {
        setChatMessages((prev) => [...(data || []), ...prev]);
      } else {
        setChatMessages(data || []);
      }
      setChatMessagesPagination({
        page: cp || page,
        totalPages: totalPages || 1,
        totalMessages: totalMessages || 0,
      });
    } catch (err) {
      console.error("Error fetching chat messages:", err);
      if (!append) setChatMessages([]);
    } finally {
      setChatMessagesLoading(false);
      setLoadingMoreMessages(false);
    }
  };

  const handleSelectChatContact = (contact) => {
    setSelectedChatContact(contact);
    setChatMessages([]);
    fetchChatMessages(contact?.user?._id || contact?._id, 1);
  };

  const handleLoadOlderMessages = () => {
    if (chatMessagesPagination.page < chatMessagesPagination.totalPages && !loadingMoreMessages) {
      const contactId = selectedChatContact?.user?._id || selectedChatContact?._id;
      fetchChatMessages(contactId, chatMessagesPagination.page + 1, true);
    }
  };

  const formatMessageTime = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatMessageDate = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) return "Today";
    if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
    return date.toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const getTimeAgo = (timestamp) => {
    if (!timestamp) return "";
    const now = new Date();
    const date = new Date(timestamp);
    const seconds = Math.floor((now - date) / 1000);
    const minutes = Math.floor(seconds / 60);
    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ago`;
    const months = Math.floor(days / 30);
    return `${months}mo ago`;
  };

  const filteredChatContacts = chatContacts.filter((contact) => {
    if (!chatContactsSearch) return true;
    const search = chatContactsSearch.toLowerCase();
    const name = (contact?.user?.nickName || "").toLowerCase();
    const phone = (contact?.user?.phone || "").toLowerCase();
    return name.includes(search) || phone.includes(search);
  });

  const handleDateChange = (e) => {
    const value = e.target.value;
    if (new Date(value) > new Date()) {
      setSelectDate("");
      return;
    }
    setSelectDate(value);
    setCallFilter("");
    //  getCallHistory(1, activeCallTab, "", value);
  };

  // ── Activity Data Fetching ──
  const fetchActivityData = async () => {
    try {
      setActivityLoading(true);
      const [activityRes, milestoneRes, configsRes] = await Promise.all([
        axios.get(
          `${process.env.REACT_APP_API_URL}/user-activity/activity-summary/${id}`
        ),
        axios.get(
          `${process.env.REACT_APP_API_URL}/user-activity/activity-milestones/${id}`
        ),
        axios.get(
          `${process.env.REACT_APP_API_URL}/user-activity/milestone-configs`
        ),
      ]);
      setActivityData(activityRes.data);
      setMilestoneData(milestoneRes.data);
      setMilestoneConfigs(configsRes.data?.configs || []);
    } catch (error) {
      console.error("Error fetching activity data:", error);
    } finally {
      setActivityLoading(false);
    }
  };

  const handleGrantMilestone = async (milestoneConfigId, tier) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/user-activity/grant-milestone`,
        {
          userId: id,
          milestoneConfigId,
          tier,
          adminId: "admin",
        }
      );
      fetchActivityData();
    } catch (error) {
      console.error("Error granting milestone:", error);
      alert(error.response?.data?.message || "Failed to grant milestone");
    }
  };

  const handleRevokeMilestone = async (milestoneId) => {
    if (!window.confirm("Are you sure you want to revoke this milestone?"))
      return;
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/user-activity/revoke-milestone`,
        {
          milestoneId,
          adminId: "admin",
        }
      );
      fetchActivityData();
    } catch (error) {
      console.error("Error revoking milestone:", error);
      alert(error.response?.data?.message || "Failed to revoke milestone");
    }
  };

  const CallLogCard = ({ logs }) => (
    <div className="rounded-2xl bg-white p-4 shadow-sm sm:p-6">
      {logs?.map((log, index) => {
        const isIncoming = userData?._id === log?.receiverUserID?._id;
        const coinText = isIncoming ? "Received" : "Spent";
        const coins = isIncoming
          ? log?.callDuration >= 120
            ? log?.coinDeducted
            : 0
          : log?.coinDeducted;
        return (
          <div
            key={index}
            className="mb-4 flex flex-col gap-3 text-gray-700 sm:flex-row sm:items-center sm:justify-between sm:gap-0"
          >
            <div className="flex items-center">
              <img
                className="mr-4 h-12 w-12 rounded-full object-cover"
                src={getAvatarUrl(
                  isIncoming
                    ? log?.callerUserID?.selected_Avatar
                    : log?.receiverUserID?.selected_Avatar,
                  isIncoming
                    ? log?.callerUserID?.nickName
                    : log?.receiverUserID?.nickName
                )}
                alt="Profile"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "https://ui-avatars.com/api/?name=User&background=c51369&color=fff";
                }}
              />
              <div>
                <h3 className="text-md font-medium tracking-wide text-navy-700">
                  {isIncoming
                    ? log?.callerUserID?.nickName
                    : log?.receiverUserID?.nickName}
                </h3>
                <p className="flex items-center gap-1 text-sm">
                  {isIncoming ? (
                    <FiArrowDownLeft className="text-green-500" />
                  ) : (
                    <FiArrowUpRight className="text-blue-500" />
                  )}
                  <span>{convertSeconds(log.callDuration)}</span>
                </p>
              </div>
            </div>
            <div className="text-right tracking-wide">
              <p className="text-sm text-gray-700">
                {new Date(log?.startTime)?.toLocaleString("en-GB", {
                  year: "numeric",
                  month: "numeric",
                  day: "numeric",
                  hour: "numeric",
                  minute: "numeric",
                  hour12: true,
                })}
              </p>
              <p
                className={`text-sm ${
                  isIncoming ? "text-green-600" : "text-[#951050]"
                }`}
              >
                {coinText} {coins} coins
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <React.Fragment>
      <EditUserpopup
        editUserModal={editUserModal}
        handleEditModalClose={() => setEditUserModal(false)}
        userDetails={userData}
        fetchUsers={getUserData}
      />
      <DeleteUserpopup
        deleteUserModal={deleteUserModal}
        handleDeleteModalClose={() => setDeleteUserModal(false)}
        userDetails={userData}
        fetchUsers={getUserData}
      />
      <ChangeAvatarModal
        open={changeAvatarModal}
        onClose={() => setChangeAvatarModal(false)}
        userDetails={userData}
        onAvatarUpdated={(newAvatarUrl) => {
          if (newAvatarUrl) {
            setUserData((prev) => ({ ...prev, selected_Avatar: newAvatarUrl }));
          }
          getUserData();
        }}
      />

      {loading ? (
        <UserDetailSkeleton />
      ) : (
        <>
          {/* Header */}
          <div className="mt-3 flex flex-col items-center justify-between gap-4 rounded-2xl bg-white p-6 shadow-sm sm:flex-row sm:gap-0 sm:p-12">
            <div className="text-center text-xl font-bold tracking-wide text-[#c51369] sm:text-left">
              {userData?.fullName}'s Profile
            </div>
            <div className="flex items-center gap-2">
              {currentTabPermissions?.permissions?.delete && (
                <button
                  onClick={() => setDeleteUserModal(true)}
                  className="flex items-center justify-center rounded-full bg-[#c51369] p-3 text-white hover:bg-[#a10f5b]"
                >
                  <BiTrash className="h-5 w-5" />
                </button>
              )}
              {currentTabPermissions?.permissions?.edit && (
                <button
                  onClick={() => setEditUserModal(true)}
                  className="flex items-center justify-center rounded-full bg-[#c51369] p-3 text-white hover:bg-[#a10f5b]"
                >
                  <FaEdit className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>

          {/* Profile Card */}
          <div className="mt-6 flex justify-center px-4">
            <div className="w-full max-w-4xl rounded-2xl bg-white p-6 shadow-sm">
              <div className="flex flex-col items-center">
                <div
                  className="group relative cursor-pointer"
                  onClick={() => {
                    if (currentTabPermissions?.permissions?.edit) {
                      setChangeAvatarModal(true);
                    }
                  }}
                  title={currentTabPermissions?.permissions?.edit ? "Click to change profile picture" : ""}
                >
                  <img
                    className="h-24 w-24 rounded-full object-cover sm:h-28 sm:w-28"
                    src={getAvatarUrl(userData?.selected_Avatar, userData?.fullName || userData?.nickName)}
                    alt="User Avatar"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(userData?.fullName || "User")}&background=c51369&color=fff`;
                    }}
                  />
                  {currentTabPermissions?.permissions?.edit && (
                    <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                      <div className="flex flex-col items-center text-white">
                        <FaCamera className="mb-1 text-lg" />
                        <span className="text-xs font-medium">Change</span>
                      </div>
                    </div>
                  )}
                </div>
                <h2 className="mt-4 text-lg font-semibold tracking-wide text-navy-700 sm:text-xl">
                  {userData?.fullName}
                </h2>
              </div>

              {/* Info Grid */}
              <div className="mt-6 grid grid-cols-1 gap-6 text-center text-base tracking-wide sm:grid-cols-2 sm:text-lg lg:grid-cols-3">
                <div>
                  <p className="font-medium text-gray-800">Nickname</p>
                  <p className="break-words text-[#c51369]">
                    {userData?.nickName}
                  </p>
                </div>
                <div>
                  <p className="font-medium text-gray-800">Phone</p>
                  <p className="break-words text-[#c51369]">
                    {userData?.phone}
                  </p>
                </div>
                <div>
                  <p className="font-medium text-gray-800">Gender</p>
                  <p className="text-[#c51369]">{userData?.gender}</p>
                </div>
                <div>
                  <p className="font-medium text-gray-800">Language</p>
                  <p className="text-[#c51369]">
                    {userData?.preferred_Language}
                  </p>
                </div>
                <div>
                  <p className="font-medium text-gray-800">Joined</p>
                  <p className="text-[#c51369]">
                    {new Date(userData?.createdAt).toLocaleDateString("en-GB")}
                  </p>
                </div>
                <div>
                  <p className="font-medium text-gray-800">Location</p>
                  <p className="break-words text-[#c51369]">
                    {userData?.current_Location}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs Section */}
          <div className="mt-8 flex justify-center px-4">
            <div className="w-full max-w-4xl">
              {/* Tabs */}
              <div className="mb-6 flex flex-wrap justify-center border-b">
                {["calls", "recharge", "login", "Ledger", "activity", "messages"].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-4 py-2 text-base font-medium sm:px-6 sm:text-lg ${
                      activeTab === tab
                        ? "border-b-2 border-[#c51369] text-[#c51369]"
                        : "text-gray-600"
                    }`}
                  >
                    {tab === "calls"
                      ? "Call History"
                      : tab === "recharge"
                      ? "Recharge History"
                      : tab === "login"
                      ? "Login History"
                      : tab === "activity"
                      ? "Activity"
                      : tab === "messages"
                      ? "Messages"
                      : "Ledger"}
                  </button>
                ))}
              </div>

              {/* Call History */}
              {activeTab === "calls" && (
                <div>
                  <div className="mb-4 flex flex-wrap justify-center gap-2 sm:justify-between sm:gap-4 md:justify-center">
                    {/* filter conditon */}
                    <div className="flex items-center gap-2">
                      <select
                        onChange={(e) => {
                          const value = e.target.value;
                          setCallFilter(value);
                          setSelectDate("");
                          getCallHistory(1, activeCallTab, value, " ");
                        }}
                        className="w-full rounded-lg border px-3 py-2 text-gray-700 focus:outline-none
                     focus:ring-2 focus:ring-[#c51369] sm:w-auto"
                      >
                        {/* <HiFilter className="text-xl text-[#c51369]" /> */}
                        <option value="">All calls</option>
                        <option value="today">Today</option>
                        <option value="last7days">Last 7 Days</option>
                        <option value="thisMonth">This Month</option>
                        <option value="lastMonth">Last Month</option>
                      </select>
                    </div>

                    {/* Audio call  */}
                    <button
                      onClick={() => setActiveCallTab("audio")}
                      disabled={callHistoryLoading}
                      className={`rounded-lg px-4 py-2 ${
                        activeCallTab === "audio"
                          ? "bg-[#c51369] text-white"
                          : "bg-gray-100 text-gray-700"
                      } ${callHistoryLoading ? "opacity-50" : ""}`}
                    >
                      Audio Calls
                    </button>

                    {/* Video call  */}
                    <button
                      onClick={() => setActiveCallTab("video")}
                      disabled={callHistoryLoading}
                      className={`rounded-lg px-4 py-2 ${
                        activeCallTab === "video"
                          ? "bg-[#c51369] text-white"
                          : "bg-gray-100 text-gray-700"
                      } ${callHistoryLoading ? "opacity-50" : ""}`}
                    >
                      Video Calls
                    </button>

                    {/* Custom date picker */}

                    <input
                      type="date"
                      value={selectdate}
                      onChange={handleDateChange}
                      max={new Date().toISOString().split("T")[0]}
                      className="rounded-lg border px-3 py-2 text-gray-700"
                    />
                  </div>

                  {callHistoryLoading ? (
                    <CallHistorySkeleton />
                  ) : callHistory?.length > 0 ? (
                    <>
                      <CallLogCard logs={callHistory} />
                      <Pagination
                        currentPage={
                          activeCallTab === "audio"
                            ? audioPagination.page
                            : videoPagination.page
                        }
                        totalPages={
                          activeCallTab === "audio"
                            ? audioPagination.totalPages
                            : videoPagination.totalPages
                        }
                        showFirstLast={false}
                        onPageChange={handleCallHistoryPageChange}
                      />
                    </>
                  ) : (
                    <p className="mb-10 text-center text-gray-500">
                      No {activeCallTab === "audio" ? "Audio" : "Video"} Calls
                      Found
                    </p>
                  )}
                </div>
              )}

              {/* Recharge History */}
              {activeTab === "recharge" &&
                (rechargeLoading ? (
                  <RechargeHistorySkeleton />
                ) : (
                  <div className="rounded-2xl bg-white p-4 shadow-sm sm:p-6">
                    {transactionHistory?.length > 0 ? (
                      <div className="w-full overflow-x-auto">
                        <table className="w-full min-w-[700px] rounded-t-2xl text-left text-sm">
                          <thead className="text-md bg-[#e0ced7] uppercase tracking-wide text-navy-900">
                            <tr className="text-center">
                              <th className="px-6 py-3">SL No.</th>
                              <th className="px-6 py-3">Date</th>
                              <th className="px-6 py-3">Time</th>
                              <th className="px-6 py-3">Coin Plan Amount</th>
                              <th className="px-6 py-3">Coupon Code Amount</th>
                              <th className="px-6 py-3">Payment Amount</th>
                              <th className="px-6 py-3">Total Coin</th>
                            </tr>
                          </thead>
                          <tbody>
                            {transactionHistory.map((data, index) => {
                              const purchaseAmount =
                                data?.coinPlan?.purchaseAmount || 0;
                              const paymentAmount = data?.amount || 0;
                              const couponAmount =
                                purchaseAmount - paymentAmount;
                              const totalCoin = data?.coinPlan?.coinAmount || 0;
                              const createdAt = new Date(data?.createdAt);
                              const date = createdAt.toLocaleDateString();
                              const time = createdAt.toLocaleTimeString();

                              return (
                                <tr
                                  key={data?._id || index}
                                  className="text-md group bg-white text-center tracking-wide text-gray-700"
                                >
                                  <td className="whitespace-nowrap border px-6 py-4">
                                    {(rechargePagination.page - 1) *
                                      rowsPerPage +
                                      (index + 1)}
                                  </td>
                                  <td className="whitespace-nowrap border px-6 py-4">
                                    {date}
                                  </td>
                                  <td className="whitespace-nowrap border px-6 py-4">
                                    {time}
                                  </td>
                                  <td className="whitespace-nowrap border px-6 py-4">
                                    ₹{purchaseAmount}
                                  </td>
                                  <td className="whitespace-nowrap border px-6 py-4">
                                    ₹{couponAmount}
                                  </td>
                                  <td className="whitespace-nowrap border px-6 py-4">
                                    ₹{paymentAmount}
                                  </td>
                                  <td className="whitespace-nowrap border px-6 py-4 text-green-600">
                                    {totalCoin}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="mb-10 text-center text-gray-500">
                        No Recharge History Found
                      </p>
                    )}

                    {/* Pagination */}
                    <Pagination
                      currentPage={rechargePagination.page}
                      totalPages={rechargePagination.totalPages}
                      showFirstLast={true}
                      onPageChange={(newPage) => recharge(newPage)}
                    />
                  </div>
                ))}

              {/* Login Activities */}
              {activeTab === "login" &&
                (loginLoading ? (
                  <LoginHistorySkeleton />
                ) : (
                  <div className="space-y-6 rounded-2xl bg-white p-4 shadow-sm sm:p-6">
                    {/* ================= ACTIVE SESSIONS ================= */}
                    <div>
                      <h3 className="mb-3 text-lg font-semibold text-gray-800">
                        Active Sessions
                      </h3>

                      {userData?.deviceInfo?.length > 0 ? (
                        <div className="space-y-3">
                          <div className="grid grid-cols-2 gap-3 rounded-xl border bg-gray-50 p-4 text-sm sm:grid-cols-4">
                            <p className="text-gray-500">Device</p>
                            <p className="text-gray-500">Status</p>
                            <p className="text-gray-500">Last login</p>
                            <p className="text-gray-500">Action </p>
                          </div>
                          {userData?.deviceInfo?.map((device, index) => (
                            <div
                              key={device.deviceId || index}
                              className="grid grid-cols-2 justify-between gap-3 rounded-xl border bg-gray-50 p-4 text-sm sm:grid-cols-4"
                            >
                              <div className="flex items-center gap-2">
                                <FaMobileAlt color="#3B82F6" />
                                <p className="text-wrap font-medium">
                                  {/* {device.brand} {device.model} */}
                                  {device.deviceName}
                                </p>
                              </div>

                              <div>
                                {/* <p className="text-gray-500">Status</p> */}
                                <span
                                  className={`rounded-full px-2 py-1 text-xs font-semibold ${
                                    device.isBlocked
                                      ? "bg-red-100 text-red-600"
                                      : "bg-green-100 text-green-600"
                                  }`}
                                >
                                  {device.isBlocked ? "Blocked" : "Active"}
                                </span>
                              </div>

                              <div>
                                <p className="font-medium">
                                  {new Date(
                                    device.lastLogInAt
                                  ).toLocaleString()}
                                </p>
                              </div>

                              <div>
                                {/* {device?.isBlocked ? (
                                  <button title="Unblock Device" onClick={}>
                                    <CgUnblock color="green" />
                                  </button>
                                ) : (
                                  <button title="Block Device">
                                    <MdBlock color="red" />
                                  </button>
                                )} */}

                                <button
                                  onClick={() =>
                                    updateBlockingStatus(
                                      device.deviceId,
                                      !device.isBlocked
                                    )
                                  }
                                >
                                  {device.isBlocked ? (
                                    <CgUnblock color="green" />
                                  ) : (
                                    <MdBlock color="red" />
                                  )}
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">
                          No active sessions found
                        </p>
                      )}
                    </div>

                    {/* ================= LOGIN HISTORY ================= */}
                    <div>
                      <h3 className="mb-3 text-lg font-semibold text-gray-800">
                        Login History
                      </h3>

                      {loginHistory?.length > 0 ? (
                        <div className="space-y-3">
                          {loginHistory.map((data, index) => (
                            <div
                              key={data?._id || index}
                              className="rounded-xl border p-4"
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                  <BiSolidLogInCircle
                                    size={24}
                                    className="mr-2 text-[#01de0a]"
                                  />
                                  <span className="font-medium">Login</span>
                                </div>

                                <span className="rounded-full bg-gradient-to-tr from-[#02550f] to-[#c51369] px-3 py-1 text-sm text-white">
                                  {new Date(data.createdAt).toLocaleString()}
                                </span>
                              </div>

                              {/* Device Info (from login history - OBJECT) */}
                              {data.deviceInfo && (
                                <div className="mt-3 grid grid-cols-2 gap-3 text-sm text-gray-700 sm:grid-cols-4">
                                  <div>
                                    <p className="text-gray-500">Device</p>
                                    <p>{data.deviceInfo.deviceName}</p>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="mb-10 text-center text-gray-500">
                          No Login Activities Found
                        </p>
                      )}

                      {/* Pagination */}
                      <Pagination
                        currentPage={loginPagination.page}
                        totalPages={loginPagination.totalPages}
                        showFirstLast={true}
                        onPageChange={(page) => handleloginHistory(page)}
                      />
                    </div>
                  </div>
                ))}

              {/* Laser Data  */}
              {activeTab === "Ledger" && (
                <>
                  <div className="mb-4 flex gap-2 border-b">
                    {["Coins", "GiftCoins", "Withdrawal"].map((tab) => (
                      <button
                        key={tab}
                        onClick={() => {
                          setLedgerTab(tab);
                          setCurrentPage(1); // reset pagination
                        }}
                        className={`px-4 py-2 text-sm font-semibold transition-all ${
                          ledgerTab === tab
                            ? "border-b-2 border-[#c51369] text-[#c51369]"
                            : "text-gray-500 hover:text-[#c51369]"
                        }`}
                      >
                        {tab}
                      </button>
                    ))}
                  </div>

                  {ledgerLoading ? (
                    <RechargeHistorySkeleton />
                  ) : ledgerTab !== "Withdrawal" ? (
                    <div>
                      {/* Dropdown + Date Picker Row */}
                      <div className="mb-4 flex flex-wrap justify-end gap-2 sm:justify-between sm:gap-4 md:justify-end">
                        {/* Filter Dropdown */}
                        <div className="relative">
                          <HiFilter className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                          <select
                            value={laserDateType}
                            onChange={(e) => {
                              setLaserDateType(e.target.value);
                              setLaserSpecificDate(null);
                              setLaserFromDate(null);
                              setLaserToDate(null);
                            }}
                            className="rounded-lg border py-2 pl-10 pr-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#c51369]"
                          >
                            <option value="">Select Filter</option>
                            <option value="specific">Specific Date</option>
                            <option value="custom">Custom Range</option>
                          </select>
                        </div>

                        {/* Specific Date */}
                        {laserDateType === "specific" && (
                          <div className="relative">
                            {/* <FiCalendar className="absolute left-8 top-2 -translate-y-1/2 text-gray-500" /> */}
                            <DatePicker
                              selected={laserSpecificDate}
                              onChange={(date) => setLaserSpecificDate(date)}
                              maxDate={new Date()}
                              dateFormat="dd/MM/yyyy"
                              placeholderText="Select Date"
                              className="rounded-lg border py-2 pl-10  text-gray-700"
                            />
                          </div>
                        )}

                        {/* Custom Dates */}
                        {laserDateType === "custom" && (
                          <div className="flex gap-2">
                            <div className="relative">
                              {/* <FiCalendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" /> */}
                              <DatePicker
                                selected={laserFromDate}
                                onChange={(date) => setLaserFromDate(date)}
                                maxDate={new Date()}
                                dateFormat="dd/MM/yyyy"
                                placeholderText="From Date"
                                className="rounded-lg border px-2 py-2 text-gray-700"
                              />
                            </div>
                            <div className="relative">
                              {/* <FiCalendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" /> */}
                              <DatePicker
                                selected={laserToDate}
                                onChange={(date) => setLaserToDate(date)}
                                maxDate={new Date()}
                                dateFormat="dd/MM/yyyy"
                                placeholderText="To Date"
                                className="rounded-lg border px-2  py-2 text-gray-700"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                      {ledgerData?.length > 0 ? (
                        <div className="mb-5 w-full overflow-x-auto">
                          <table className="w-full min-w-[700px] rounded-t-2xl text-left text-sm">
                            <thead className="text-md bg-[#e0ced7] uppercase tracking-wide text-navy-900">
                              <tr className="text-center">
                                <th className="px-6 py-3">SL No.</th>
                                <th className="px-6 py-3">Date</th>
                                <th className="px-6 py-3">Time</th>
                                <th className="px-6 py-3">Credited </th>
                                <th className="px-6 py-3">Debited </th>
                                <th className="px-6 py-3">Transaction Type </th>
                                <th className="px-6 py-3">Total Balance</th>
                              </tr>
                            </thead>
                            <tbody>
                              {ledgerData?.map((data, index) => {
                                const createdAt = new Date(data?.createdAt);
                                const date = createdAt.toLocaleDateString();
                                const time = createdAt.toLocaleTimeString();

                                return (
                                  <tr
                                    key={data?._id || index}
                                    className="text-md group bg-white text-center tracking-wide text-gray-700"
                                  >
                                    <td className="whitespace-nowrap border px-6 py-4">
                                      {(currentPage - 1) * rowsPerPage +
                                        (index + 1)}
                                    </td>
                                    <td className="whitespace-nowrap border px-6 py-4">
                                      {date}
                                    </td>
                                    <td className="whitespace-nowrap border px-6 py-4">
                                      {time}
                                    </td>
                                    <td className="whitespace-nowrap border px-6 py-4 text-green-600">
                                      {data?.creditedCoins?.toFixed(2)}
                                    </td>
                                    <td className="whitespace-nowrap border px-6 py-4 text-red-600">
                                      {data?.debitedCoins?.toFixed(2)}
                                    </td>
                                    <td className="whitespace-nowrap border px-6 py-4">
                                      {data?.transactionType}
                                    </td>
                                    <td className="whitespace-nowrap border px-6 py-4 font-semibold">
                                      {data?.totalBalance?.toFixed(2)}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="mb-10 text-center text-gray-500">
                          No laser data Found
                        </p>
                      )}
                      <Pagination
                        currentPage={ledgerPagination.page}
                        totalPages={ledgerPagination.totalPages}
                        onPageChange={(newPage) => getLedgerData(newPage)}
                      />
                    </div>
                  ) : (
                    <div>
                      {withdrawalData?.length > 0 ? (
                        <div className="mb-5 w-full overflow-x-auto">
                          <table className="w-full min-w-[700px] rounded-t-2xl text-left text-sm">
                            <thead className="text-md bg-[#e0ced7] uppercase tracking-wide text-navy-900">
                              <tr className="text-center">
                                <th className="px-6 py-3">SL No.</th>
                                <th className="px-6 py-3">Date</th>
                                {/* <th className="px-6 py-3">Time</th> */}
                                <th className="px-6 py-3">Requested Amt.</th>
                                <th className="px-6 py-3">Disbursed Amt.</th>
                                <th className="px-6 py-3">Status</th>
                                <th className="px-6 py-3">Total Balance</th>
                              </tr>
                            </thead>
                            <tbody>
                              {withdrawalData?.map((data, index) => {
                                const createdAt = new Date(data?.createdAt);
                                const date = createdAt.toLocaleDateString();
                                const color =
                                  data.paymentStatus === "approved"
                                    ? "green"
                                    : data?.paymentStatus === "rejected"
                                    ? "red"
                                    : "orange";
                                return (
                                  <tr
                                    key={data?._id || index}
                                    className="text-md group bg-white text-center tracking-wide text-gray-700"
                                  >
                                    <td className="whitespace-nowrap border px-6 py-4">
                                      {(currentPage - 1) * rowsPerPage +
                                        (index + 1)}
                                    </td>
                                    <td className="whitespace-nowrap border px-6 py-4">
                                      {date}
                                    </td>
                                    <td className="whitespace-nowrap border px-6 py-4 ">
                                      {data?.amount?.toFixed(2)}
                                    </td>
                                    <td
                                      className={`whitespace-nowrap border px-6 py-4 text-${color}-600`}
                                    >
                                      {data?.disbursedAmount?.toFixed(2)}
                                    </td>
                                    <td
                                      className={`whitespace-nowrap border px-6 py-4 text-${color}-600 capitalize `}
                                    >
                                      <p
                                        className={`bg-${color}-50 rounded-full px-3 py-1 text-xs`}
                                      >
                                        {data?.paymentStatus}
                                      </p>
                                    </td>
                                    <td
                                      className={`whitespace-nowrap border px-6 py-4`}
                                    >
                                      {data?.ledgerTotalGiftCoins?.toFixed(2) ||
                                        "-"}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="mb-10 text-center text-gray-500">
                          No withdrawal transactions Found
                        </p>
                      )}
                      <Pagination
                        currentPage={ledgerPagination.page}
                        totalPages={ledgerPagination.totalPages}
                        onPageChange={(newPage) => getLedgerData(newPage)}
                      />
                    </div>
                  )}
                </>
              )}

              {/* Activity Tab */}
              {activeTab === "activity" && (
                <div>
                  {activityLoading ? (
                    <div className="flex items-center justify-center py-20">
                      <div className="h-8 w-8 animate-spin rounded-full border-4 border-pink-200 border-t-[#c51369]"></div>
                      <p className="ml-3 text-gray-500">Loading activity data...</p>
                    </div>
                  ) : activityData ? (
                    <div className="space-y-6">
                      {/* ── Summary Stats Grid ── */}
                      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                        <div className="rounded-xl bg-gradient-to-br from-pink-500 to-pink-600 p-4 text-white shadow-lg">
                          <p className="text-sm opacity-80">Total Calls</p>
                          <p className="text-2xl font-bold">
                            {activityData.oneToOneCalls?.totalCalls || 0}
                          </p>
                          <p className="mt-1 text-xs opacity-70">
                            Audio: {activityData.oneToOneCalls?.totalAudioCalls || 0} | Video: {activityData.oneToOneCalls?.totalVideoCalls || 0}
                          </p>
                        </div>
                        <div className="rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 p-4 text-white shadow-lg">
                          <p className="text-sm opacity-80">Games Played</p>
                          <p className="text-2xl font-bold">
                            {activityData.games?.totalGamesPlayed || 0}
                          </p>
                          <p className="mt-1 text-xs opacity-70">
                            Unique: {activityData.games?.uniqueGames || 0}
                          </p>
                        </div>
                        <div className="rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 p-4 text-white shadow-lg">
                          <p className="text-sm opacity-80">Online Hours</p>
                          <p className="text-2xl font-bold">
                            {activityData.gangLeader?.totalOnlineHours || 0}h
                          </p>
                          <p className="mt-1 text-xs opacity-70">
                            {activityData.gangLeader?.isGangLeader ? "Gang Leader" : "Not a leader"}
                          </p>
                        </div>
                        <div className="rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 p-4 text-white shadow-lg">
                          <p className="text-sm opacity-80">Total Gifts</p>
                          <p className="text-2xl font-bold">
                            {(activityData.gifts?.totalGiftsSent || 0) + (activityData.gifts?.totalGiftsReceived || 0)}
                          </p>
                          <p className="mt-1 text-xs opacity-70">
                            Sent: {activityData.gifts?.totalGiftsSent || 0} | Received: {activityData.gifts?.totalGiftsReceived || 0}
                          </p>
                        </div>
                      </div>

                      {/* ── 1-1 Call Details ── */}
                      <div className="rounded-2xl bg-white p-5 shadow-sm">
                        <h3 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-800">
                          <span className="inline-block h-5 w-1 rounded bg-pink-500"></span>
                          1-1 Call Statistics
                        </h3>
                        <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                          <div className="rounded-lg bg-gray-50 p-3">
                            <p className="text-xs text-gray-500">Calls Made</p>
                            <p className="text-xl font-bold text-gray-800">{activityData.oneToOneCalls?.callsMade || 0}</p>
                          </div>
                          <div className="rounded-lg bg-gray-50 p-3">
                            <p className="text-xs text-gray-500">Calls Received</p>
                            <p className="text-xl font-bold text-gray-800">{activityData.oneToOneCalls?.callsReceived || 0}</p>
                          </div>
                          <div className="rounded-lg bg-gray-50 p-3">
                            <p className="text-xs text-gray-500">Unique Users</p>
                            <p className="text-xl font-bold text-gray-800">{activityData.oneToOneCalls?.uniqueUsersCalled || 0}</p>
                          </div>
                          <div className="rounded-lg bg-gray-50 p-3">
                            <p className="text-xs text-gray-500">Total Duration</p>
                            <p className="text-xl font-bold text-gray-800">{convertSeconds(activityData.oneToOneCalls?.totalCallDuration || 0)}</p>
                          </div>
                          <div className="rounded-lg bg-gray-50 p-3">
                            <p className="text-xs text-gray-500">Avg Duration</p>
                            <p className="text-xl font-bold text-gray-800">{activityData.oneToOneCalls?.avgCallDuration || 0}s</p>
                          </div>
                        </div>
                      </div>

                      {/* ── Group Call & Gang Details ── */}
                      <div className="rounded-2xl bg-white p-5 shadow-sm">
                        <h3 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-800">
                          <span className="inline-block h-5 w-1 rounded bg-purple-500"></span>
                          Group & Gang Activity
                        </h3>
                        <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                          <div className="rounded-lg bg-gray-50 p-3">
                            <p className="text-xs text-gray-500">Groups Joined</p>
                            <p className="text-xl font-bold text-gray-800">{activityData.groupCalls?.totalGroupCallsJoined || 0}</p>
                          </div>
                          <div className="rounded-lg bg-gray-50 p-3">
                            <p className="text-xs text-gray-500">Unique Groups</p>
                            <p className="text-xl font-bold text-gray-800">{activityData.groupCalls?.uniqueGroupCalls || 0}</p>
                          </div>
                          <div className="rounded-lg bg-gray-50 p-3">
                            <p className="text-xs text-gray-500">Gifts in Groups</p>
                            <p className="text-xl font-bold text-gray-800">{activityData.groupCalls?.totalGiftsInGroup || 0}</p>
                          </div>
                          {activityData.gangLeader?.isGangLeader && (
                            <>
                              <div className="rounded-lg bg-purple-50 p-3">
                                <p className="text-xs text-purple-500">Gangs Created</p>
                                <p className="text-xl font-bold text-purple-700">{activityData.gangLeader?.totalGangsCreated || 0}</p>
                              </div>
                              <div className="rounded-lg bg-purple-50 p-3">
                                <p className="text-xs text-purple-500">Games Completed</p>
                                <p className="text-xl font-bold text-purple-700">{activityData.gangLeader?.totalGamesCompleted || 0}</p>
                              </div>
                              <div className="rounded-lg bg-purple-50 p-3">
                                <p className="text-xs text-purple-500">Ranks Earned</p>
                                <p className="text-xl font-bold text-purple-700">{activityData.gangLeader?.totalRanksEarned || 0}</p>
                              </div>
                            </>
                          )}
                        </div>
                      </div>

                      {/* ── Game Details ── */}
                      <div className="rounded-2xl bg-white p-5 shadow-sm">
                        <h3 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-800">
                          <span className="inline-block h-5 w-1 rounded bg-blue-500"></span>
                          Game Statistics
                        </h3>
                        <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                          <div className="rounded-lg bg-gray-50 p-3">
                            <p className="text-xs text-gray-500">Total Games</p>
                            <p className="text-xl font-bold text-gray-800">{activityData.games?.totalGamesPlayed || 0}</p>
                          </div>
                          <div className="rounded-lg bg-gray-50 p-3">
                            <p className="text-xs text-gray-500">Unique Games</p>
                            <p className="text-xl font-bold text-gray-800">{activityData.games?.uniqueGames || 0}</p>
                          </div>
                          <div className="rounded-lg bg-gray-50 p-3">
                            <p className="text-xs text-gray-500">As Leader</p>
                            <p className="text-xl font-bold text-gray-800">{activityData.games?.gamesAsLeader || 0}</p>
                          </div>
                          <div className="rounded-lg bg-gray-50 p-3">
                            <p className="text-xs text-gray-500">As Co-Host</p>
                            <p className="text-xl font-bold text-gray-800">{activityData.games?.gamesAsCoHost || 0}</p>
                          </div>
                          <div className="rounded-lg bg-gray-50 p-3">
                            <p className="text-xs text-gray-500">Coins from Games</p>
                            <p className="text-xl font-bold text-green-600">{activityData.games?.totalCoinsFromGames || 0}</p>
                          </div>
                        </div>
                      </div>

                      {/* ── Masti Details ── */}
                      <div className="rounded-2xl bg-white p-5 shadow-sm">
                        <h3 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-800">
                          <span className="inline-block h-5 w-1 rounded bg-amber-500"></span>
                          Masti Activity
                        </h3>
                        <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                          <div className="rounded-lg bg-gray-50 p-3">
                            <p className="text-xs text-gray-500">Rooms Hosted</p>
                            <p className="text-xl font-bold text-gray-800">{activityData.masti?.totalMastiHosted || 0}</p>
                          </div>
                          <div className="rounded-lg bg-gray-50 p-3">
                            <p className="text-xs text-gray-500">Times Joined</p>
                            <p className="text-xl font-bold text-gray-800">{activityData.masti?.totalMastiJoined || 0}</p>
                          </div>
                          <div className="rounded-lg bg-gray-50 p-3">
                            <p className="text-xs text-gray-500">Unique Rooms</p>
                            <p className="text-xl font-bold text-gray-800">{activityData.masti?.uniqueMastiRooms || 0}</p>
                          </div>
                          <div className="rounded-lg bg-gray-50 p-3">
                            <p className="text-xs text-gray-500">Gifts Sent</p>
                            <p className="text-xl font-bold text-gray-800">{activityData.masti?.giftsSentInMasti || 0}</p>
                          </div>
                        </div>
                      </div>

                      {/* ── General Info ── */}
                      <div className="rounded-2xl bg-white p-5 shadow-sm">
                        <h3 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-800">
                          <span className="inline-block h-5 w-1 rounded bg-gray-500"></span>
                          General
                        </h3>
                        <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                          <div className="rounded-lg bg-gray-50 p-3">
                            <p className="text-xs text-gray-500">Login Sessions</p>
                            <p className="text-xl font-bold text-gray-800">{activityData.general?.totalLoginSessions || 0}</p>
                          </div>
                          <div className="rounded-lg bg-gray-50 p-3">
                            <p className="text-xs text-gray-500">Account Age</p>
                            <p className="text-xl font-bold text-gray-800">{activityData.general?.accountAgeDays || 0} days</p>
                          </div>
                          <div className="rounded-lg bg-gray-50 p-3">
                            <p className="text-xs text-gray-500">Rating</p>
                            <p className="text-xl font-bold text-amber-500">⭐ {activityData.general?.rating || 0}</p>
                          </div>
                          <div className="rounded-lg bg-gray-50 p-3">
                            <p className="text-xs text-gray-500">Coin Balance</p>
                            <p className="text-xl font-bold text-gray-800">{activityData.general?.coinAmount || 0}</p>
                          </div>
                          <div className="rounded-lg bg-gray-50 p-3">
                            <p className="text-xs text-gray-500">Converted Wallet</p>
                            <p className="text-xl font-bold text-green-600">₹{(activityData.general?.convertedWallet || 0).toFixed(2)}</p>
                          </div>
                          <div className="rounded-lg bg-gray-50 p-3">
                            <p className="text-xs text-gray-500">Gift Wallet</p>
                            <p className="text-xl font-bold text-pink-600">{activityData.general?.giftWallet || 0}</p>
                          </div>
                        </div>
                      </div>

                      {/* ── Milestones Management ── */}
                      <div className="rounded-2xl bg-white p-5 shadow-sm">
                        <h3 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-800">
                          <IoMdTrophy className="text-amber-500" />
                          Milestones & Rewards
                        </h3>
                        {milestoneData?.milestones?.length > 0 ? (
                          <div className="space-y-4">
                            {milestoneData.milestones.map((config) => (
                              <div key={config._id} className="rounded-xl border border-gray-100 bg-gray-50 p-4">
                                <div className="mb-3 flex items-center justify-between">
                                  <div>
                                    <h4 className="font-semibold text-gray-800">{config.displayName}</h4>
                                    <p className="text-xs text-gray-500">Reset: {config.resetPeriod}</p>
                                  </div>
                                  <span className={`rounded-full px-2 py-0.5 text-xs ${config.isActive ? "bg-green-100 text-green-600" : "bg-gray-200 text-gray-500"}`}>
                                    {config.isActive ? "Active" : "Inactive"}
                                  </span>
                                </div>
                                <div className="space-y-2">
                                  {(config.tiers || []).sort((a, b) => a.tier - b.tier).map((tier) => {
                                    const grantedTier = (config.grantedTiers || []).find((g) => g.tier === tier.tier);
                                    const isGranted = !!grantedTier;
                                    return (
                                      <div key={tier.tier} className={`flex items-center justify-between rounded-lg border p-3 ${
                                        isGranted ? "border-green-200 bg-green-50" : "border-gray-100 bg-white"
                                      }`}>
                                        <div className="flex items-center gap-3">
                                          <div className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold ${
                                            isGranted ? "bg-green-500 text-white" : "bg-gray-200 text-gray-500"
                                          }`}>
                                            {isGranted ? "✓" : tier.tier}
                                          </div>
                                          <div>
                                            <p className="text-sm font-medium text-gray-800">Threshold: {tier.threshold}</p>
                                            <p className="text-xs text-gray-500">
                                              {tier.rewardType === "coins" ? `${tier.rewardAmount} Coins` : tier.rewardType === "badge" ? tier.badgeName || "Badge" : "No Reward"}
                                            </p>
                                          </div>
                                        </div>
                                        <div>
                                          {isGranted ? (
                                            <button
                                              onClick={() => {
                                                const milestoneRecord = (milestoneData?.milestones || []).flatMap(c =>
                                                  (c.grantedTiers || []).filter(g => g.tier === tier.tier)
                                                );
                                                // Find the actual milestone record ID from UserActivityMilestone collection
                                                // For now we'll need to fetch it
                                                handleRevokeMilestone(grantedTier._id);
                                              }}
                                              className="rounded-lg bg-red-50 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-100"
                                            >
                                              Revoke
                                            </button>
                                          ) : (
                                            <button
                                              onClick={() => handleGrantMilestone(config._id, tier.tier)}
                                              className="rounded-lg bg-[#c51369] px-3 py-1.5 text-xs font-medium text-white hover:bg-pink-700"
                                            >
                                              Grant
                                            </button>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="py-10 text-center">
                            <IoMdTrophy className="mx-auto mb-2 text-4xl text-gray-300" />
                            <p className="text-gray-500">No milestone configs defined</p>
                            <p className="text-sm text-gray-400">Create milestones from the Milestone Config page</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <p className="py-10 text-center text-gray-500">No activity data available</p>
                  )}
                </div>
              )}

              {/* Messages Tab */}
              {activeTab === "messages" && (
                <div>
                  {chatContactsLoading ? (
                    <div className="flex items-center justify-center py-20">
                      <div className="h-8 w-8 animate-spin rounded-full border-4 border-pink-200 border-t-[#c51369]"></div>
                      <p className="ml-3 text-gray-500">Loading messages...</p>
                    </div>
                  ) : !selectedChatContact ? (
                    /* ===== Chat Contacts List ===== */
                    <div>
                      {/* Search */}
                      <div className="relative mb-4">
                        <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search by name or phone..."
                          value={chatContactsSearch}
                          onChange={(e) => setChatContactsSearch(e.target.value)}
                          className="w-full rounded-xl border border-gray-200 bg-gray-50 py-2.5 pl-10 pr-4 text-sm outline-none transition-all focus:border-[#c51369] focus:bg-white focus:ring-2 focus:ring-pink-100"
                        />
                      </div>

                      {filteredChatContacts.length === 0 ? (
                        <div className="py-16 text-center">
                          <IoMdChatbubbles className="mx-auto mb-3 text-5xl text-gray-300" />
                          <p className="text-gray-500">
                            {chatContactsSearch ? "No matching chats found" : "No chat history found"}
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-1">
                          {filteredChatContacts.map((contact, idx) => {
                            const user = contact?.user;
                            const avatar = getAvatarUrl(user?.selected_Avatar, user?.nickName);
                            return (
                              <div
                                key={user?._id || idx}
                                onClick={() => handleSelectChatContact(contact)}
                                className="group flex cursor-pointer items-center gap-3 rounded-xl px-3 py-3 transition-all hover:bg-pink-50"
                              >
                                <img
                                  src={avatar}
                                  alt={user?.nickName}
                                  className="h-11 w-11 rounded-full object-cover ring-2 ring-gray-100 group-hover:ring-pink-200"
                                />
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center justify-between">
                                    <p className="truncate text-sm font-semibold text-gray-800">
                                      {user?.nickName || "Unknown User"}
                                    </p>
                                    <span className="ml-2 whitespace-nowrap text-xs text-gray-400">
                                      {getTimeAgo(contact?.lastMessageTimestamp)}
                                    </span>
                                  </div>
                                  <div className="flex items-center justify-between">
                                    <p className="truncate text-xs text-gray-500">
                                      {contact?.lastMessageType === "image"
                                        ? "📷 Image"
                                        : contact?.lastMessageType === "video"
                                        ? "🎥 Video"
                                        : contact?.lastMessage || "No messages"}
                                    </p>
                                    {contact?.unseenMessagesCount > 0 && (
                                      <span className="ml-2 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#c51369] px-1.5 text-[10px] font-bold text-white">
                                        {contact.unseenMessagesCount}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ) : (
                    /* ===== Chat Thread View ===== */
                    <div>
                      {/* Thread Header */}
                      <div className="mb-4 flex items-center gap-3 rounded-xl bg-white p-3 shadow-sm">
                        <button
                          onClick={() => {
                            setSelectedChatContact(null);
                            setChatMessages([]);
                          }}
                          className="flex h-8 w-8 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
                        >
                          ←
                        </button>
                        <img
                          src={getAvatarUrl(
                            selectedChatContact?.user?.selected_Avatar,
                            selectedChatContact?.user?.nickName
                          )}
                          alt={selectedChatContact?.user?.nickName}
                          className="h-10 w-10 rounded-full object-cover ring-2 ring-pink-100"
                        />
                        <div>
                          <p className="text-sm font-semibold text-gray-800">
                            {selectedChatContact?.user?.nickName || "Unknown User"}
                          </p>
                          <p className="text-xs text-gray-400">
                            {selectedChatContact?.user?.phone || ""}
                          </p>
                        </div>
                        <div className="ml-auto text-right">
                          <p className="text-xs text-gray-400">
                            {chatMessagesPagination.totalMessages} messages
                          </p>
                        </div>
                      </div>

                      {/* Messages */}
                      {chatMessagesLoading ? (
                        <div className="flex items-center justify-center py-20">
                          <div className="h-8 w-8 animate-spin rounded-full border-4 border-pink-200 border-t-[#c51369]"></div>
                          <p className="ml-3 text-gray-500">Loading messages...</p>
                        </div>
                      ) : chatMessages.length === 0 ? (
                        <div className="py-16 text-center">
                          <IoMdChatbubbles className="mx-auto mb-3 text-5xl text-gray-300" />
                          <p className="text-gray-500">No messages found</p>
                        </div>
                      ) : (
                        <div
                          className="rounded-2xl border border-gray-100 bg-gradient-to-b from-gray-50 to-white p-4 shadow-sm"
                          style={{ maxHeight: "600px", overflowY: "auto" }}
                        >
                          {/* Load Older */}
                          {chatMessagesPagination.page < chatMessagesPagination.totalPages && (
                            <div className="mb-4 text-center">
                              <button
                                onClick={handleLoadOlderMessages}
                                disabled={loadingMoreMessages}
                                className="rounded-full bg-white px-4 py-1.5 text-xs font-medium text-[#c51369] shadow-sm ring-1 ring-gray-200 transition-all hover:bg-pink-50 hover:ring-pink-200 disabled:opacity-50"
                              >
                                {loadingMoreMessages ? (
                                  <span className="flex items-center gap-1">
                                    <span className="h-3 w-3 animate-spin rounded-full border-2 border-pink-200 border-t-[#c51369]"></span>
                                    Loading...
                                  </span>
                                ) : (
                                  "↑ Load older messages"
                                )}
                              </button>
                            </div>
                          )}

                          {/* Message Bubbles with Date Separators */}
                          <div className="space-y-1">
                            {chatMessages.map((msg, index) => {
                              const isSender = msg.sender === id;
                              const prevMsg = index > 0 ? chatMessages[index - 1] : null;
                              const showDateSeparator =
                                !prevMsg ||
                                formatMessageDate(msg.timestamp) !==
                                  formatMessageDate(prevMsg?.timestamp);

                              return (
                                <React.Fragment key={msg._id || index}>
                                  {showDateSeparator && (
                                    <div className="flex items-center justify-center py-2">
                                      <span className="rounded-full bg-gray-200/80 px-3 py-0.5 text-[10px] font-medium text-gray-500">
                                        {formatMessageDate(msg.timestamp)}
                                      </span>
                                    </div>
                                  )}
                                  <div
                                    className={`flex ${
                                      isSender ? "justify-end" : "justify-start"
                                    }`}
                                  >
                                    <div
                                      className={`relative max-w-[75%] rounded-2xl px-3.5 py-2 shadow-sm ${
                                        isSender
                                          ? "rounded-br-md bg-gradient-to-br from-[#c51369] to-[#a8105a] text-white"
                                          : "rounded-bl-md bg-white text-gray-800 ring-1 ring-gray-100"
                                      }`}
                                    >
                                      {msg.messageType === "image" ? (
                                        <div className="overflow-hidden rounded-lg">
                                          <img
                                            src={msg.message}
                                            alt="Shared image"
                                            className="max-h-52 max-w-full rounded-lg object-cover"
                                            onError={(e) => {
                                              e.target.style.display = "none";
                                              e.target.parentNode.innerHTML =
                                                '<p class="text-xs italic opacity-70">📷 Image</p>';
                                            }}
                                          />
                                        </div>
                                      ) : msg.messageType === "video" ? (
                                        <p
                                          className={`text-xs italic ${
                                            isSender ? "text-pink-200" : "text-gray-400"
                                          }`}
                                        >
                                          🎥 Video message
                                        </p>
                                      ) : (
                                        <p className="whitespace-pre-wrap break-words text-[13px] leading-relaxed">
                                          {msg.message}
                                        </p>
                                      )}
                                      <div
                                        className={`mt-0.5 flex items-center gap-1 ${
                                          isSender ? "justify-end" : "justify-start"
                                        }`}
                                      >
                                        <span
                                          className={`text-[10px] ${
                                            isSender
                                              ? "text-pink-200"
                                              : "text-gray-400"
                                          }`}
                                        >
                                          {formatMessageTime(msg.timestamp)}
                                        </span>
                                        {isSender && (
                                          <span
                                            className={`text-[10px] ${
                                              msg.seen
                                                ? "text-blue-300"
                                                : "text-pink-200"
                                            }`}
                                          >
                                            {msg.seen ? "✓✓" : "✓"}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </React.Fragment>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </React.Fragment>
  );
};

export default UserDetailsPage;
